import { useCallback, useEffect, useState } from "react";
import { t } from "i18next";
import i18n from "@/common/i18n";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox } from "@/components/ui/Combobox";
import {
  AlarmClockCheck,
  AudioLines,
  BookType,
  FileText,
  Languages,
  Link,
  Mic,
  MousePointerClick,
  Rocket,
  Shield,
  type LucideIcon,
} from "lucide-react";
import { OnboardingMicTest } from "@/components/ui/onboarding/onboarding-mic-test";
import { useAuthStore } from "@/store/auth.store";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { useAppSettingsStore } from "@/store/appSettings.store";
import { useTemplateStore } from "@/store/templates.store";
import { TranscriptionLanguageWhisperAvailable } from "@/hooks/transcriber/types";
import { interfaceLanguageOptions } from "@/common/constants";
import { DefaultRecordingTemplates } from "@/common/enums/enums";
import {
  markOnboarded,
  markPostStepSeen,
  DEFAULT_TEMPLATE_KEY,
} from "@/lib/onboarding";

interface OnboardingCardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStartRecording?: () => void;
  postStep?: boolean;
}

const StepBadge = ({ number }: { number: number }): JSX.Element => (
  <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full bg-[#0099FF] text-[13px] font-bold text-white">
    {number}
  </span>
);

const StepTitle = ({
  number,
  title,
  subtitle,
}: {
  number: number;
  title: string;
  subtitle?: string;
}): JSX.Element => (
  <div className="flex items-start gap-[14px]">
    <StepBadge number={number} />
    <div className="space-y-1">
      <p className="text-xl font-bold text-black">{title}</p>
      {subtitle && <p className="text-base text-[#4A5565]">{subtitle}</p>}
    </div>
  </div>
);

const TipCard = ({
  imageSrc,
  title,
  description,
}: {
  imageSrc: string;
  title: string;
  description: string;
}): JSX.Element => (
  <div className="w-[232px] rounded-[10px] border border-[#E6E6E6]">
    <div className="flex h-[181px] items-center justify-center bg-[#F3F3F3]">
      <img alt={title} className="h-[181px] w-[231px]" src={imageSrc} />
    </div>
    <div className="space-y-1 p-4 text-center">
      <p className="text-base font-bold text-black">{title}</p>
      <p className="text-xs font-medium text-black">{description}</p>
    </div>
  </div>
);

const TipsStep = ({
  onBack,
  onContinue,
}: {
  onBack: () => void;
  onContinue: () => void;
}): JSX.Element => (
  <div className="flex h-full flex-col">
    <DialogTitle className="sr-only">{t("onboarding.tips.title")}</DialogTitle>
    <div className="mb-9 text-center">
      <p className="text-[30px] font-extrabold text-black">
        {t("onboarding.tips.title")}
      </p>
      <p className="text-lg font-medium text-black">
        {t("onboarding.tips.subtitle")}
      </p>
    </div>

    <div className="flex justify-center gap-[66px]">
      <TipCard
        description={t("onboarding.tips.card1.description")}
        imageSrc="/images/onboarding/tip-phone-distance.png"
        title={t("onboarding.tips.card1.title")}
      />
      <TipCard
        description={t("onboarding.tips.card2.description")}
        imageSrc="/images/onboarding/tip-lock-screen.png"
        title={t("onboarding.tips.card2.title")}
      />
      <TipCard
        description={t("onboarding.tips.card3.description")}
        imageSrc="/images/onboarding/tip-mobile-quality.png"
        title={t("onboarding.tips.card3.title")}
      />
    </div>

    <div className="mt-auto flex justify-center gap-4 pt-6">
      <Button
        className="h-[42px] w-[99px] rounded-[10px] border border-[#DBDBDB] bg-white font-bold text-[#545454]"
        variant="outline"
        onClick={onBack}
      >
        {t("onboarding.tips.back")}
      </Button>
      <Button
        className="h-[42px] w-[99px] rounded-[10px] bg-primary font-bold"
        onClick={onContinue}
      >
        {t("onboarding.form.submit")}
      </Button>
    </div>
  </div>
);

const SummaryRow = ({
  icon: Icon,
  label,
  value,
  iconClassName = "h-[17px] w-[17px]",
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  iconClassName?: string;
}): JSX.Element => (
  <div className="flex items-center justify-between border-b border-[#B0B0B0] px-[33px] py-[10px]">
    <div className="flex items-center gap-[10px]">
      <span className="flex h-[27px] w-[27px] items-center justify-center rounded-full bg-[#E6F3FD]">
        <Icon className={`${iconClassName} text-[#0082EE]`} />
      </span>
      <span className="text-base font-bold text-black">{label}</span>
    </div>
    <span className="text-base font-medium text-[#545454]">{value}</span>
  </div>
);

const SummaryStep = ({
  template,
  noteLanguageLabel,
  displayLanguageLabel,
  isRealtimeEnabled,
  onMaybeLater,
  onStartRecording,
}: {
  template: string;
  noteLanguageLabel: string;
  displayLanguageLabel: string;
  isRealtimeEnabled: boolean;
  onMaybeLater: () => void;
  onStartRecording: () => void;
}): JSX.Element => (
  <div className="flex h-full flex-col">
    <DialogTitle className="sr-only">
      {t("onboarding.summary.title")}
    </DialogTitle>
    <div className="flex flex-col items-center gap-[10px]">
      <img
        alt=""
        className="h-[99px] w-[92px]"
        src="/images/onboarding/onboarding-summary.png"
      />
      <div className="text-center">
        <p className="text-[30px] font-extrabold text-black">
          {t("onboarding.summary.title")}
        </p>
        <p className="text-lg font-medium text-black">
          {t("onboarding.summary.subtitle")}
        </p>
      </div>
    </div>

    <div className="mx-auto mt-[20px] w-[651px]">
      <SummaryRow
        icon={Mic}
        iconClassName="h-[19px] w-[19px]"
        label={t("onboarding.summary.microphone")}
        value={t("onboarding.summary.micValue")}
      />
      <SummaryRow
        icon={Languages}
        label={t("onboarding.summary.displayLanguage")}
        value={displayLanguageLabel}
      />
      <SummaryRow
        icon={BookType}
        label={t("onboarding.summary.noteLanguage")}
        value={noteLanguageLabel}
      />
      <SummaryRow
        icon={FileText}
        iconClassName="h-[19px] w-[19px]"
        label={t("onboarding.summary.template")}
        value={template}
      />
      <SummaryRow
        icon={AudioLines}
        iconClassName="h-[19px] w-[19px]"
        label={t("onboarding.summary.mode")}
        value={
          isRealtimeEnabled
            ? t("onboarding.summary.modeRealtime")
            : t("onboarding.summary.modeNonRealtime")
        }
      />
    </div>

    <div className="mt-[36px] flex justify-between">
      <Button
        className="h-[42px] w-[185px] rounded-[10px] border border-[#DBDBDB] bg-white font-bold text-[#545454]"
        variant="outline"
        onClick={onMaybeLater}
      >
        {t("onboarding.summary.maybeLater")}
      </Button>
      <Button
        className="h-[42px] w-[257px] rounded-[10px] bg-primary font-bold"
        onClick={onStartRecording}
      >
        {t("onboarding.summary.startRecording")}
      </Button>
    </div>
  </div>
);

const PostRecordingStep = ({
  onMaybeLater,
  onInstallExtension,
}: {
  onMaybeLater: () => void;
  onInstallExtension: () => void;
}): JSX.Element => {
  const Card = ({
    icon: Icon,
    title,
  }: {
    icon: LucideIcon;
    title: string;
  }): JSX.Element => (
    <div className="flex items-center justify-center rounded-[10px] bg-[#E6F3FD] px-5 py-5">
      <div className="flex flex-col items-center gap-[13px]">
        <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full bg-white">
          <Icon
            className="h-[28px] w-[28px] text-[#0399FF]"
            strokeWidth={2.4}
          />
        </span>
        <p className="text-[20px] font-semibold text-black/70">{title}</p>
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col">
      <DialogTitle className="sr-only">
        {t("onboarding.postRecording.title")}
      </DialogTitle>
      <div className="mb-[20px] text-center">
        <p className="text-[30px] font-extrabold text-black">
          {t("onboarding.postRecording.title")}
        </p>
        <p className="text-lg font-medium text-black">
          {t("onboarding.postRecording.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-[20px]">
        <Card
          icon={MousePointerClick}
          title={t("settings.chromeExtension.features.oneClick")}
        />
        <Card
          icon={Link}
          title={t("settings.chromeExtension.features.emrSupport")}
        />
        <Card
          icon={AlarmClockCheck}
          title={t("settings.chromeExtension.features.saveTime")}
        />
        <Card icon={Rocket} title={t("onboarding.postRecording.setup")} />
      </div>

      <div className="mt-auto flex justify-between pt-6">
        <Button
          className="h-[42px] w-[121px] rounded-[10px] bg-[#E6E6E6] font-bold text-black"
          variant="outline"
          onClick={onMaybeLater}
        >
          {t("onboarding.summary.maybeLater")}
        </Button>
        <Button
          className="h-[42px] w-[358px] rounded-[10px] bg-primary font-bold"
          onClick={onInstallExtension}
        >
          <span className="flex h-[27px] w-[27px] items-center justify-center rounded-full bg-[#E6F3FD]">
            <Mic className="h-[19px] w-[19px] text-[#0082EE]" />
          </span>
          {t("onboarding.postRecording.install")}
        </Button>
      </div>
    </div>
  );
};

const TranscriptionModeOptions = (): JSX.Element => {
  const { isRealtimeEnabled, saveIsRealtimeEnabled } =
    useRecordingSettingsStore();

  const OptionRow = ({
    selected,
    title,
    hint,
    recommended = false,
    onSelect,
  }: {
    selected: boolean;
    title: string;
    hint: string;
    recommended?: boolean;
    onSelect: () => void;
  }): JSX.Element => (
    <button
      className="flex w-full items-center gap-3 border-b border-[#E0E0E0] px-4 py-3 last:border-b-0 text-left"
      type="button"
      onClick={onSelect}
    >
      <span
        className={`h-[15px] w-[15px] shrink-0 rounded-full ${
          selected ? "bg-[#0082EE]" : "border-2 border-[#B4B7BC]"
        }`}
      />
      <span className="flex-1">
        <span className="flex items-center gap-2 text-sm font-bold text-black">
          {title}
          {recommended && (
            <span className="rounded-full bg-[#E6F6EF] px-2 py-0.5 text-[11px] font-semibold text-[#039757]">
              {t("onboarding.form.recommended")}
            </span>
          )}
        </span>
        <span className="block text-[11px] text-[#545454]">{hint}</span>
      </span>
    </button>
  );

  return (
    <div className="overflow-hidden rounded-[10px] border border-[#B0B0B0]">
      <OptionRow
        recommended
        hint={t("onboarding.form.transcriptionRealtimeHint")}
        selected={isRealtimeEnabled}
        title={t("onboarding.form.transcriptionRealtime")}
        onSelect={() => {
          saveIsRealtimeEnabled(true);
        }}
      />
      <OptionRow
        hint={t("onboarding.form.transcriptionNonRealtimeHint")}
        selected={!isRealtimeEnabled}
        title={t("onboarding.form.transcriptionNonRealtime")}
        onSelect={() => {
          saveIsRealtimeEnabled(false);
        }}
      />
    </div>
  );
};

export const OnboardingCard = ({
  open,
  onOpenChange,
  onStartRecording,
  postStep = false,
}: OnboardingCardProps): JSX.Element => {
  const [step, setStep] = useState<"greeting" | "form" | "tips" | "summary">(
    "greeting"
  );
  const [template, setTemplate] = useState<string>(
    DefaultRecordingTemplates.SOAP.toString()
  );
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const cardHeight = postStep
    ? "h-[602px]"
    : step === "form"
      ? "h-[702px]"
      : step === "tips" || step === "summary"
        ? "h-[602px]"
        : "h-[540px]";

  const { userData } = useAuthStore.getState();
  const { inputLanguage, saveInputLanguage } =
    useRecordingSettingsStore.getState();
  const { setInputLanguage } = useAppSettingsStore.getState();
  const { templates, fetchAndSetTemplates } = useTemplateStore.getState();

  useEffect(() => {
    if (userData?.id) {
      void fetchAndSetTemplates({ userId: userData.id });
    }
    setContainer(document.getElementById("app-content"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const handleTemplateChange = (value: string): void => {
    setTemplate(value);
  };

  const handleFinish = (): void => {
    localStorage.setItem(DEFAULT_TEMPLATE_KEY, template);
    markOnboarded();
    onOpenChange(false);
  };

  const inputLanguageValue =
    inputLanguage && inputLanguage !== "auto" ? inputLanguage : "eng";

  const templateOptions = [
    ...Object.values(DefaultRecordingTemplates).map((name) => ({
      value: name,
      label: name,
    })),
    ...templates.map((tpl) => ({ value: String(tpl.id), label: tpl.name })),
  ];

  const resetForm = useCallback(() => {
    setStep("greeting");
    setTemplate(DefaultRecordingTemplates.SOAP.toString());
  }, []);

  if (!open) return <></>;

  if (postStep) {
    return (
      <Dialog
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) {
            markPostStepSeen();
            resetForm();
          }
        }}
      >
        <DialogContent
          className={`fixed w-[919px] max-w-full max-h-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[30px] p-8 overflow-y-auto ${cardHeight}`}
          container={container}
          overlayClassName="bg-black/30"
        >
          <PostRecordingStep
            onInstallExtension={() => {
              markPostStepSeen();
              window.open(
                "https://chromewebstore.google.com/detail/bbgcekhigafammpphfipefklildgjmfh",
                "_blank",
                "noopener"
              );
              onOpenChange(false);
            }}
            onMaybeLater={() => {
              markPostStepSeen();
              onOpenChange(false);
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) resetForm();
      }}
    >
      <DialogContent
        className={`absolute w-[919px] max-w-full max-h-full left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[30px] p-8 overflow-y-auto ${cardHeight}`}
        container={container}
        overlayClassName="bg-black/30"
      >
        {step === "greeting" ? (
          <>
            <div className="absolute left-8 top-7">
              <Select
                value={i18n.language}
                onValueChange={(lang) => {
                  void i18n.changeLanguage(lang);
                }}
              >
                <SelectTrigger className="w-[150px] h-10 rounded-[10px] border border-[#B0B0B0] bg-white focus:ring-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {interfaceLanguageOptions.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex h-full flex-col items-center justify-center gap-6 py-12 text-center">
              <img
                alt="ROSC"
                className="w-[239px]"
                src="/images/logos/logo-full.svg"
              />
              <DialogTitle className="text-[40px] font-extrabold text-black">
                {t("onboarding.greeting.title", {
                  name: `${userData?.firstname ?? ""} ${
                    userData?.lastname ?? ""
                  }`.trim(),
                })}
              </DialogTitle>
              <p className="max-w-[568px] text-xl leading-6 text-[#4A5565]">
                {t("onboarding.greeting.description")}
              </p>
              <Button
                className="h-[42px] rounded-[10px] bg-primary px-6 font-bold"
                onClick={() => {
                  setStep("form");
                }}
              >
                {t("onboarding.getStarted")}
              </Button>
            </div>
          </>
        ) : step === "form" ? (
          <div className="flex h-full flex-col">
            <DialogTitle className="sr-only">
              {t("onboarding.form.title")}
            </DialogTitle>
            <div className="mb-8 text-center">
              <p className="text-[30px] font-extrabold text-black">
                {t("onboarding.form.title")}
              </p>
              <p className="text-lg font-medium text-black">
                {t("onboarding.form.subtitle")}
              </p>
            </div>

            <div className="flex flex-col gap-[34px] xl:flex-row">
              <div className="flex-1 rounded-[15px] border border-black/10 p-5">
                <div className="flex flex-col gap-5">
                  <StepTitle
                    number={1}
                    subtitle={t("onboarding.form.checkMicrophoneSubtitle")}
                    title={t("onboarding.form.checkMicrophone")}
                  />
                  <OnboardingMicTest />

                  <StepTitle
                    number={2}
                    title={t("onboarding.form.transcriptionMode")}
                  />
                  <TranscriptionModeOptions />

                  <div className="flex items-center gap-2 text-[#545454]">
                    <Shield className="h-4 w-4" />
                    <span className="text-xs font-semibold">
                      {t("onboarding.form.privacyNote")}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex-1 rounded-[15px] border border-black/10 p-5">
                <div className="flex flex-col gap-5">
                  <StepTitle
                    number={3}
                    title={t("onboarding.form.displayLanguage")}
                  />
                  <Select
                    value={i18n.language}
                    onValueChange={(lang) => {
                      void i18n.changeLanguage(lang);
                    }}
                  >
                    <SelectTrigger className="h-[42px] rounded-[10px] border-[#B0B0B0] bg-white focus:ring-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {interfaceLanguageOptions.map((lang) => (
                        <SelectItem key={lang.value} value={lang.value}>
                          {lang.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <StepTitle
                    number={4}
                    title={t("onboarding.form.noteLanguage")}
                  />
                  <Combobox
                    items={TranscriptionLanguageWhisperAvailable}
                    value={inputLanguageValue}
                    onValueChange={({ value }) => {
                      saveInputLanguage(value);
                      setInputLanguage(value);
                    }}
                  />

                  <StepTitle
                    number={5}
                    title={t("onboarding.form.defaultTemplate")}
                  />
                  <Select value={template} onValueChange={handleTemplateChange}>
                    <SelectTrigger className="h-[42px] rounded-[10px] border-[#B0B0B0] bg-white focus:ring-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {templateOptions.map((tpl) => (
                        <SelectItem key={tpl.value} value={tpl.value}>
                          {tpl.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="mt-auto flex justify-end pt-6">
              <Button
                className="h-[42px] w-[99px] rounded-[10px] bg-primary font-bold"
                onClick={() => {
                  setStep("tips");
                }}
              >
                {t("onboarding.form.submit")}
              </Button>
            </div>
          </div>
        ) : step === "tips" ? (
          <TipsStep
            onBack={() => {
              setStep("form");
            }}
            onContinue={() => {
              setStep("summary");
            }}
          />
        ) : (
          <SummaryStep
            template={template}
            displayLanguageLabel={
              interfaceLanguageOptions.find(
                (lang) => lang.value === i18n.language
              )?.label ?? ""
            }
            isRealtimeEnabled={
              useRecordingSettingsStore.getState().isRealtimeEnabled
            }
            noteLanguageLabel={
              TranscriptionLanguageWhisperAvailable.find(
                (lang) => lang.value === inputLanguageValue
              )?.label ?? ""
            }
            onMaybeLater={handleFinish}
            onStartRecording={() => {
              handleFinish();
              onStartRecording?.();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
